import { OrdemServico } from '@/types';
import { normalizeApiData } from './ordensServicoDataNormalizer';
import { validateAndProcessProduct, validateFinalProduct } from './ordensServicoProductProcessor';
import { processOrderHours } from './ordensServicoHoursProcessor';
import { createFallbackOrdemServico } from './ordensServicoFallback';
import { getValorHoraClienteVigente } from './ordensServicoValueQueries';
import { convertHoursToDecimal } from './timeConversion';
import { OrdensSyncPreloadContext, resolveClienteValorHora } from './ordensServicoSyncPreload';
import { processClientWithSubdivision } from './ordensServicoClientProcessor';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const convertApiToLocal = async (
  apiData: any,
  impostoPercentual: number,
  preload?: OrdensSyncPreloadContext
): Promise<OrdemServico> => {
  try {
    const normalizedData = normalizeApiData(apiData);
    const produtoProcessado = validateAndProcessProduct(normalizedData.produto, normalizedData.numero);
    const { horasExecutadas, horasFaturadas } = processOrderHours(apiData, normalizedData.numero);
    const horasExecDec = convertHoursToDecimal(horasExecutadas);
    const produtoParaRegra =
      produtoProcessado && produtoProcessado !== 'ERRO_PRODUTO_NAO_EXTRAIDO'
        ? produtoProcessado
        : 'Produto não informado';

    const { clienteFinal: cliDesm, empresaIdFinal: empIdDesm, valorHoraRegra } = await processClientWithSubdivision(
      apiData,
      normalizedData.cliente,
      produtoParaRegra,
      normalizedData.tipoProjeto,
      horasExecDec,
      normalizedData.numero,
      { skipAuditoria: !!preload },
      normalizedData.dataAbertura
    );
    const clienteFinal = cliDesm;
    const empresaIdFinal =
      empIdDesm !== undefined && empIdDesm !== null && String(empIdDesm) !== ''
        ? Number(empIdDesm) || empIdDesm
        : apiData.empresa_id;

    let valorFaturamento = 0;
    let clienteValorHora = 0;

    const horasFaturadasDecimal = typeof horasFaturadas === 'number'
      ? horasFaturadas
      : convertHoursToDecimal(horasFaturadas.toString());

    if (
      normalizedData.tipoChamado === 'atividadeprojeto' ||
      normalizedData.tipoChamado === 'orcamento'
    ) {
      valorFaturamento = 0;
      clienteValorHora = 0;
    } else if (horasFaturadasDecimal === 0) {
      valorFaturamento = 0;
      clienteValorHora = 0;
    } else {
      try {
        if (valorHoraRegra != null && valorHoraRegra > 0) {
          clienteValorHora = valorHoraRegra;
        } else if (preload) {
          clienteValorHora = resolveClienteValorHora(clienteFinal, preload);
        } else {
          clienteValorHora = await getValorHoraClienteVigente(
            null,
            clienteFinal,
            normalizedData.dataAbertura,
            { usarValorMaisRecente: true }
          );
        }

        valorFaturamento = horasFaturadasDecimal * clienteValorHora;
        valorFaturamento = Math.round(valorFaturamento * 100) / 100;
      } catch {
        valorFaturamento = 0;
        clienteValorHora = 0;
      }
    }

    const imposto = Math.round(valorFaturamento * impostoPercentual * 100) / 100;
    let valorAPagar = valorFaturamento - imposto;
    const statusApi = (normalizedData.status || apiData.status?.descricao || '').toLowerCase();
    if (statusApi === 'cancelada') {
      valorAPagar = 0;
    }

    const produtoFinal = produtoProcessado && produtoProcessado !== 'Produto não informado'
      ? produtoProcessado
      : null;
    validateFinalProduct(produtoFinal, normalizedData.numero);

    const ordemServico: OrdemServico = {
      id: normalizedData.id,
      numero: normalizedData.numero,
      cliente: clienteFinal,
      contato: normalizedData.contato,
      produto: produtoFinal || 'ERRO_PRODUTO_NAO_EXTRAIDO',
      operador: normalizedData.operador,
      natureza: normalizedData.natureza,
      status: normalizedData.status as 'aguardandoaprovacao' | 'aprovada' | 'cancelada' | 'rascunho' | 'emrevisao' | 'reprovada',
      dataAbertura: normalizedData.dataAbertura,
      dataFechamento: normalizedData.dataFechamento,
      horasExecutadas,
      horasFaturadas,
      valorPorHora: clienteValorHora,
      valorTotal: valorFaturamento,
      valorFaturamento: valorFaturamento,
      imposto: imposto,
      valorAPagar: valorAPagar,
      dataFormatada: normalizedData.dataFormatada,
      observacoes: normalizedData.observacoes,
      empresa: clienteFinal,
      projeto: normalizedData.projeto,
      tipoProjeto: normalizedData.tipoProjeto,
      tipoChamado: normalizedData.tipoChamado,
      incluidoPor: normalizedData.incluidoPor,
      estatisticasChamado: normalizedData.estatisticasChamado,
      api_data: {
        ...apiData,
        empresa_id: empresaIdFinal,
        empresa:
          apiData.empresa && typeof apiData.empresa === 'object'
            ? { ...apiData.empresa, nome: clienteFinal, id: empresaIdFinal }
            : { nome: clienteFinal, id: empresaIdFinal },
        projeto: {
          id: apiData.projeto?.id || null,
          nome: normalizedData.projeto,
          fechado: normalizedData.projetoFechado
        },
        projeto_fechado: normalizedData.projetoFechado,
        produto: {
          id: apiData.produto?.id || null,
          nome: produtoFinal || apiData.produto?.nome || null
        }
      },
    };

    return ordemServico;
  } catch (error) {
    console.error(`Erro ao converter OS ${apiData.id}:`, error);
    return createFallbackOrdemServico(apiData, error as Error);
  }
};
